import { useTranslation } from "@sg/i18n";
import { Skeleton } from "@sg/ui";
import { IconArrowRight, IconHome } from "@tabler/icons-react";
import clsx from "clsx";
import memoize from "lodash/memoize";
import { Fragment, useMemo } from "react";
import { UseQueryResult } from "react-query";
import { Link, Params, PathMatch, generatePath, useNavigate, useParams } from "react-router-dom";
import useBreadcrumbs, { BreadcrumbData, BreadcrumbsRoute } from "use-react-router-breadcrumbs";
import { findRoute } from "~/routes";
import { BREADCRUMB_ROUTES, ROUTE_CONFIG } from "~/routes/config";
import { RoleV2 } from "~/types";
import { isAuthorized } from "~/util/role";
interface QueryBreadcrumbProps<T> {
  match: PathMatch;
  query: (id?: string) => UseQueryResult<T>;
  getId?: (match: PathMatch) => string;
  onSwitcherChange?: (id: string) => { route: string; params: Params<string> };
  getLabel: (data: T) => string;
}

export function TitleBreadcrumb({ title }: { title: string }) {
  return title ?? "";
}

export function PathBreadcrumb({ match, field }: { match: PathMatch; field: string }) {
  return match.params[field] ?? "";
}

export function QuerySwitcherBreadcrumb<T>({
  match,
  query,
  getId,
  getLabel,
  onSwitcherChange,
}: QueryBreadcrumbProps<T>) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const { data = [], isLoading, error } = query(getId ? getId(match) : undefined);

  if (isLoading) {
    return <Skeleton className="w-[75px]" />;
  }

  if (error) {
    return <div>{t("error")}</div>;
  }

  return (
    <div className="flex items-center">
      <select
        id="location"
        name="location"
        className="cursor-pointer block border-none w-full py-0 px-2 pr-8 text-base focus:!outline-none sm:text-sm bg-background focus:!ring-0"
        value={getId ? getId(match) : undefined}
        onChange={(e) => {
          if (onSwitcherChange) {
            const newPath = onSwitcherChange(e.target.value);
            navigate(generatePath(newPath.route, { ...params, ...newPath.params }));
          }
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {(data as (T & { id: string })[])?.map((d) => (
          <option value={d.id} key={d.id}>
            {getLabel(d)}
          </option>
        ))}
      </select>
      <button className="bg-background py-0 my-0 text-xs px-1">
        <IconArrowRight className="w-5 h-5" />
      </button>
    </div>
  );
}

export function QueryBreadcrumb<T>({ match, query, getId, getLabel }: QueryBreadcrumbProps<T>) {
  const { t } = useTranslation();
  const { data, isLoading, error } = query(getId ? getId(match) : undefined);

  if (isLoading) {
    return <Skeleton className="w-[75px]" />;
  }

  return (
    <div>
      <>
        {data && getLabel(data)}
        {error && t("error")}
      </>
    </div>
  );
}

export default function Breadcrumbs() {
  const { t } = useTranslation();
  const routesList = useMemo(
    () => [...BREADCRUMB_ROUTES, ...ROUTE_CONFIG],
    []
  ) as unknown as BreadcrumbsRoute<string>[];
  const breadcrumbs = useBreadcrumbs(routesList);
  const findMemoizedRoute = memoize(findRoute);

  if (breadcrumbs.length <= 1) {
    return <></>;
  }

  return (
    <nav className="flex pb-6" aria-label="Breadcrumb">
      <ol
        role="list"
        className="flex sm:flex-row flex-col items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-4"
      >
        <li>
          <div>
            <Link to="/" className="text-gray-500 hover:text-foreground">
              <IconHome className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
              <span className="sr-only">{t("nav:home")}</span>
            </Link>
          </div>
        </li>
        {breadcrumbs.slice(1).map((crumb) => {
          const { breadcrumb, key, hideCrumb, roles } = crumb as BreadcrumbData<string> & {
            hideCrumb?: boolean;
            roles?: RoleV2[];
          };

          if (hideCrumb && breadcrumbs.length > 2) return <Fragment key={key} />;

          const route = findMemoizedRoute(key);
          const authorized = isAuthorized(roles);
          return (
            <li key={key}>
              <div className="flex items-center">
                <svg
                  className="flex-shrink-0 h-5 w-5 text-gray-300 dark:text-gray-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                </svg>
                <Link
                  to={key}
                  className={clsx("ml-4 text-sm font-medium text-gray-500 hover:text-foreground", {
                    "pointer-events-none opacity-70": !route || !authorized,
                  })}
                >
                  {breadcrumb}
                </Link>
              </div>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
